import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import { omnichannelInboxApi } from "../../services/omnichannelInboxApi";

export default function OmnichannelRealtimeHub() {
  const navigate = useNavigate();
  const { user, echo } = useContext(AuthContext);
  const [popup, setPopup] = useState(null);
  const presenceQueueRef = useRef(Promise.resolve());

  // Queue of escalation alerts — each is shown as a stacked Snackbar.
  // We keep a queue so rapid-fire escalations don't swallow each other.
  const [escalationQueue, setEscalationQueue] = useState([]);
  const escalationIdRef = useRef(0);

  const syncPresence = useCallback((status) => {
    presenceQueueRef.current = presenceQueueRef.current
      .catch(() => null)
      .then(() => omnichannelInboxApi.updateMyAvailability({ status }))
      .catch((error) => {
        console.warn("Presence sync failed", error);
        return null;
      });

    return presenceQueueRef.current;
  }, []);

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    let mounted = true;
    const heartbeat = window.setInterval(() => {
      if (mounted) {
        syncPresence("available");
      }
    }, 30000);

    syncPresence("available");

    const handleBeforeUnload = () => {
      syncPresence("offline");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      mounted = false;
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.clearInterval(heartbeat);
      syncPresence("offline");
    };
  }, [syncPresence, user?.id]);

  useEffect(() => {
    if (!user?.id || !echo) {
      return undefined;
    }

    const channelName = `omnichannel.user.${user.id}`;
    const channel = echo.private(channelName);

    channel.listen(".omnichannel.voice.inbound-popup", (payload) => {
      setPopup(payload || null);
    });

    channel.listen(".omnichannel.escalation.triggered", (payload) => {
      const id = ++escalationIdRef.current;
      setEscalationQueue((prev) => [...prev, { id, ...payload }]);
    });

    channel.listen(".omnichannel.conversation.updated", (payload) => {
      window.dispatchEvent(
        new CustomEvent("omnichannel:conversation-updated", {
          detail: payload || {},
        })
      );
    });

    return () => {
      try {
        channel.stopListening(".omnichannel.voice.inbound-popup");
        channel.stopListening(".omnichannel.escalation.triggered");
        channel.stopListening(".omnichannel.conversation.updated");
        echo.leaveChannel(`private-${channelName}`);
      } catch (_) {
        // noop
      }
    };
  }, [echo, user?.id]);

  const dismissEscalation = (id) =>
    setEscalationQueue((prev) => prev.filter((a) => a.id !== id));

  const handleOpenInbox = () => {
    const conversationId = popup?.conversation_id;
    setPopup(null);
    navigate(`/omnichannel/inbox${conversationId ? `?conversation=${conversationId}` : ""}`);
  };

  if (!popup && escalationQueue.length === 0) {
    return null;
  }

  return (
    <>
      {/* ── Voice call pop-up dialog ───────────────────────────────────────── */}
      {popup && (
      <Dialog open onClose={() => setPopup(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PhoneInTalkRoundedIcon color="success" />
          Inbound Voice Call
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body1" fontWeight={800}>
              {popup?.customer?.name || "Unknown caller"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {popup?.customer?.phone || "No caller number supplied"}
            </Typography>
            {popup?.workstream?.name ? (
              <Typography variant="body2" color="text.secondary">
                Workstream: {popup.workstream.name}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              Status: {popup?.status || "ringing"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopup(null)}>Dismiss</Button>
          <Button variant="contained" color="success" onClick={handleOpenInbox}>
            Open Inbox
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* ── Escalation alert toasts (stacked, bottom-right) ───────────────── */}
      {escalationQueue.map((alert, index) => (
        <Snackbar
          key={alert.id}
          open
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ bottom: { xs: 16 + index * 110, sm: 24 + index * 110 } }}
        >
          <Alert
            severity="error"
            icon={<WarningAmberRoundedIcon fontSize="inherit" />}
            sx={{
              width: 360,
              boxShadow: 6,
              "& .MuiAlert-message": { flex: 1 },
            }}
            action={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Button
                  size="small"
                  color="inherit"
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  onClick={() => {
                    dismissEscalation(alert.id);
                    navigate(
                      `/omnichannel/inbox${alert.conversation_id ? `?conversation=${alert.conversation_id}` : ""}`
                    );
                  }}
                >
                  Open
                </Button>
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => dismissEscalation(alert.id)}
                >
                  <CloseRoundedIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            }
          >
            <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
              {alert.rule_label || "Escalation triggered"}
            </Typography>
            <Typography variant="caption" display="block" mt={0.25}>
              {alert.customer_name}
              {alert.assigned_name ? ` · Assigned: ${alert.assigned_name}` : " · Unassigned"}
            </Typography>
          </Alert>
        </Snackbar>
      ))}
    </>
  );
}
